import {Component, OnInit} from '@angular/core';
import {AuthService} from "../../../auth/services/auth.service";
import {Timestamp} from "firebase/firestore";
import {Router} from "@angular/router";

import {HeaderService} from "../../../tandem-core/services/header.service";

import {SpendingPlanTemplate} from "../../models/spending-plan-template";
import {SpendingPlan} from "../../models/spending-plan";
import {SpendingPlanService} from "../../services/spending-plan.service";
import {SpendingPlanTemplateService} from "../../services/spending-plan-template.service";
import {TandemUser} from "../../../auth/models/tandem-user";
import {CashFlowTemplate} from "../../../cash-flows/models/cash-flow-template";
import {LocalStorageService} from "../../../tandem-core/services/local-storage.service";
import {DialogService} from "../../../tandem-core/services/dialog.service";
import {Subject, takeUntil} from "rxjs";
import {StatementTemplateService} from "../../../tandem-core/services/statement-template.service";
import {DatePipe} from "@angular/common";
import {TandemDatePipe} from "../../../tandem-core/directives/tandem-date.pipe";

@Component({
  selector: 'tandem-create-spending-plan',
  templateUrl: './create-spending-plan.component.html',
  styleUrls: ['./create-spending-plan.component.scss']
})
export class CreateSpendingPlanComponent implements OnInit {

  userId: string | undefined;
  spendingPlan: SpendingPlan | undefined;
  mostRecent: boolean = false;
  user: TandemUser | null = null;

  private destroy$ = new Subject<void>();
  hasPrompted = false;
  shouldPrompt = true;

  constructor(private auth: AuthService,
              private spendingPlanService: SpendingPlanService,
              private spendingPlanTemplateService: StatementTemplateService,
              private headerService: HeaderService,
              private dialogService: DialogService,
              private localStorageService: LocalStorageService,
              private datePipe: TandemDatePipe,
              private router: Router) {
    this.mostRecent = router.url.includes('most-recent');
  }

  ngOnInit(): void {
    this.spendingPlan = undefined;
    this.auth.$user.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.userId = user?.id;
      this.user = user;
      if (user) {
        if (this.mostRecent) {
          this.shouldPrompt = false;
          this.localStorageService.removeItem('createSpendingPlan');
          this.spendingPlanService.getLatest(user.uid).pipe(takeUntil(this.destroy$)).subscribe(latestSP => {
            if (this.spendingPlan === undefined) {
              if (latestSP) {
                const previousStartDate = new Date(latestSP.startDate.toDate()); // Assuming startDate and endDate are stored as Timestamp
                const previousEndDate = new Date(latestSP.endDate.toDate());
                const differenceInDays = (Number(previousEndDate) - Number(previousStartDate)) / (1000 * 60 * 60 * 24); // milliseconds to days

                const newStartDate = new Date(latestSP.endDate.toDate());

                const newEndDate = new Date(newStartDate);
                newEndDate.setDate(newStartDate.getDate() + differenceInDays);

                this.spendingPlan = {
                  dateCreated: Timestamp.now(),
                  dateModified: Timestamp.now(),
                  title: `${this.spendingPlanService.getTrackingToolName('single', this.user)} from ${this.datePipe.transform(newStartDate)} to ${this.datePipe.transform(newEndDate)}`,
                  startDate: Timestamp.fromDate(newStartDate),
                  endDate: Timestamp.fromDate(newEndDate),
                  userId: this.userId!,
                  netCashFlow: latestSP.netCashFlow,
                  revenueSections: latestSP.revenueSections,
                  expenseSections: latestSP.expenseSections,
                  totalRevenue: latestSP.totalRevenue,
                  totalExpenses: latestSP.totalExpenses,
                  payYourselfFirstPercentage: latestSP.payYourselfFirstPercentage,
                  visibleToCoach: latestSP.visibleToCoach
                }
              } else {
                this.useTemplateForUser(user); // Latest was not found
              }
            }
          });
        } else {
          if (!this.localStorageService.getItem('createSpendingPlan')) {
            this.useTemplateForUser(user);
          } else {
            if (this.shouldPrompt && !this.hasPrompted) {
              this.dialogService.openConfirmDialog('Pick Up Where You Left Off',
                `Looks like you were working on a new ${this.spendingPlanService.getTrackingToolName('single', this.user)} before leaving the page. Would you like to pick up where you left off?`,
                'Yes Please', 'No, Start Fresh').afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
                if (confirmed) {
                  this.spendingPlan = this.localStorageService.getItem('createSpendingPlan');
                  if (this.spendingPlan) {
                    this.spendingPlan.startDate = new Timestamp(this.spendingPlan.startDate.seconds, this.spendingPlan.startDate.nanoseconds);
                    this.spendingPlan.endDate = new Timestamp(this.spendingPlan.endDate.seconds, this.spendingPlan.endDate.nanoseconds);
                    this.spendingPlan.dateCreated = new Timestamp(this.spendingPlan.dateCreated.seconds, this.spendingPlan.dateCreated.nanoseconds);
                    this.spendingPlan.dateModified = new Timestamp(this.spendingPlan.dateModified.seconds, this.spendingPlan.dateModified.nanoseconds);
                  }
                } else {
                  this.useTemplateForUser(user);
                }
              });
              this.hasPrompted = true;
            }
          }
        }
      }
      this.headerService.setTitle(this.user?.customTTMetadata?.spendingPlanName ? 'Create ' + this.user.customTTMetadata.spendingPlanName : 'Spending Plan')
      this.headerService.setSubtitle(this.getTrackingToolDescription())
      this.headerService.setBreadcrumbs([
        {
          label: this.getTrackingToolName('plural'),
          route: 'spending-plans'
        },
        {
          label: 'Create',
        }
      ])
    });
  }

  getTrackingToolName(variant: 'single' | 'plural'): string {
    if (variant === 'single') {
      return this.user?.customTTMetadata?.spendingPlanName ? this.user.customTTMetadata.spendingPlanName : 'Spending Plan';
    } else {
      return this.user?.customTTMetadata?.spendingPlanNamePlural ? `${this.user.customTTMetadata.spendingPlanNamePlural}` : 'Spending Plans';
    }
  }

  getTrackingToolDescription() {
    return !!this.user?.customTTMetadata?.spendingPlanDescription ? this.user.customTTMetadata.spendingPlanDescription : 'A prioritized plan for allocating the cash you expect to bring in over a period of time.'
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCreate(fp: SpendingPlan) {
    this.spendingPlanService.add(fp).then(() => {
      this.localStorageService.removeItem('createSpendingPlan');
      this.router.navigate(['spending-plans']);
    })
  }

  private useTemplateForUser(user: TandemUser) {
    this.shouldPrompt = false;
    this.spendingPlanTemplateService.getTemplateForUser(user).pipe(takeUntil(this.destroy$)).subscribe((template: SpendingPlanTemplate) => {
      if (this.spendingPlan === undefined) {
        let startDate = new Date();
        let endDate = new Date(startDate);
        endDate.setMonth(startDate.getMonth() + 1);
        this.spendingPlan = {
          dateCreated: Timestamp.now(),
          dateModified: Timestamp.now(),
          title: `${this.spendingPlanService.getTrackingToolName('single', this.user)} from ${this.datePipe.transform(startDate)} to ${this.datePipe.transform(endDate)}`,
          startDate: Timestamp.fromDate(startDate),
          endDate: Timestamp.fromDate(endDate),
          userId: this.userId!,
          revenueSections: template.revenueSections.filter(sec => sec.categories.length > 0),
          expenseSections: template.expenseSections.filter(sec => sec.categories.length > 0),
          netCashFlow: 0,
          totalRevenue: 0,
          totalExpenses: 0,
          visibleToCoach: true
        }
      }
    })
  }
}
